import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import {
  AutocompleteSelectInput,
  CheckboxInput,
  ConfirmActionModal,
  CtaButton,
} from '@tassomutuo-backoffice/ui';
import { TeamGroup, TeamMember } from '@tassomutuo-backoffice/types';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';

type TeamMembersTableProps = {
  rowCount: number;
  isPending: boolean;
  paginationModel: GridPaginationModel;
  setPaginationModel: (paginationModel: GridPaginationModel) => void;
  users: TeamMember[];
  onCanReceiveLeadsCheckboxChange: (row: TeamMember) => void;
  onCanReceiveLeadsQuintoCheckboxChange: (row: TeamMember) => void;
  onCanReceiveLeadsPrestitoCheckboxChange: (row: TeamMember) => void;
  teamGroups: TeamGroup[];
  onTeamGroupChange: (row: TeamMember, groupId: string) => void;
  onRemoveMemberFromTeamGroup: (row: TeamMember, groupId: string) => void;
  onRemoveMemberFromTeam: (row: TeamMember) => void;
  onConfirmSendAlertNotificationToMember: (row: TeamMember) => Promise<void>;
  disabled: boolean;
};

const TeamMembersTable = ({
  rowCount,
  isPending,
  paginationModel,
  setPaginationModel,
  users,
  onCanReceiveLeadsCheckboxChange,
  onCanReceiveLeadsQuintoCheckboxChange,
  onCanReceiveLeadsPrestitoCheckboxChange,
  teamGroups,
  onTeamGroupChange,
  onRemoveMemberFromTeamGroup,
  onRemoveMemberFromTeam,
  disabled,
  onConfirmSendAlertNotificationToMember,
}: TeamMembersTableProps) => {
  const [anchorElActionsMenu, setAnchorElActionsMenu] =
    useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [memberToSendNotification, setMemberToSendNotification] =
    useState<TeamMember | null>(null);
  const actionsMenuOpen = Boolean(anchorElActionsMenu);

  const handleActionMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    member: TeamMember
  ) => {
    setAnchorElActionsMenu(event.currentTarget);
    setSelectedMember(member);
  };

  const handleCloseActionsMenu = () => {
    setAnchorElActionsMenu(null);
    setSelectedMember(null);
  };

  const handleRemoveMemberFromTeam = () => {
    if (selectedMember) {
      onRemoveMemberFromTeam(selectedMember);
      handleCloseActionsMenu();
    }
  };

  const groupsSelectOptions = teamGroups.map((group) => ({
    label: group.name,
    value: group.id,
  }));

  const rowCountRef = useRef(rowCount);

  const rowCountMemoized = useMemo(() => {
    if (rowCount !== undefined && rowCount !== 0) {
      rowCountRef.current = rowCount;
    }
    return rowCountRef.current;
  }, [rowCount]);

  const tableColumns: GridColDef<TeamMember>[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
    },
    {
      field: 'group',
      sortable: false,
      headerName: 'Gruppo',
      flex: 1,
      headerAlign: 'center',
      renderCell: ({ row }) => {
        // check if the user is in a group
        const groupId = teamGroups.find((group) =>
          group.members?.includes(row.id)
        )?.id;

        if (groupId) {
          return (
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <Box width={42}>
                {!row.team && (
                  <IconButton
                    disabled={disabled}
                    onClick={() => onRemoveMemberFromTeamGroup(row, groupId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              <p>
                {groupsSelectOptions.find((g) => g.value === groupId)?.label}
              </p>
            </Box>
          );
        }

        if (groupsSelectOptions.length === 0) {
          return <p>Nessun gruppo disponibile</p>;
        }

        return (
          <AutocompleteSelectInput
            disabled={disabled}
            selectOptions={groupsSelectOptions}
            value={groupId}
            /* eslint-disable-next-line */
            // @ts-expect-error
            onChange={(
              event: React.SyntheticEvent,
              /* eslint-disable-next-line */
              value: any
            ) => {
              onTeamGroupChange(row, value?.value || '');
            }}
          />
        );
      },
    },

    {
      field: 'canReceiveLeads',
      sortable: false,
      headerName: 'Assegna Lead',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 150,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" justifyContent="center" width="100%">
            <CheckboxInput
              disabled={disabled}
              checked={row?.canReceiveLeads}
              onChange={() => onCanReceiveLeadsCheckboxChange(row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'canReceiveLeadsQuinto',
      sortable: false,
      headerName: 'Assegna Lead Quinto',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 170,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" justifyContent="center" width="100%">
            <CheckboxInput
              disabled={disabled}
              checked={row?.canReceiveLeadsQuinto}
              onChange={() => onCanReceiveLeadsQuintoCheckboxChange(row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'canReceiveLeadsPrestito',
      sortable: false,
      headerName: 'Assegna Lead Prestito',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 180,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" justifyContent="center" width="100%">
            <CheckboxInput
              disabled={disabled}
              checked={row?.canReceiveLeadsPrestito}
              onChange={() => onCanReceiveLeadsPrestitoCheckboxChange(row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'oldestLead',
      sortable: false,
      headerName: 'Lead non aggiornate',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 150,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" justifyContent="center" width="100%">
            <p>{row?.statistics?.oldestLead}</p>
          </Box>
        );
      },
    },

    {
      field: 'gCalendarAuthorization',
      sortable: true,
      headerName: 'GCalendar',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 100,
      type: 'boolean',
      valueGetter: ({ row }) => !!row?.googleCalendar?.authorized,
      renderCell: ({ row }) => (
        <Tooltip
          title={
            row?.googleCalendar?.authorized
              ? 'Google Calendar connesso'
              : 'Google Calendar non connesso'
          }
        >
          {row?.googleCalendar?.authorized ? (
            <CheckCircleIcon color="success" />
          ) : (
            <ReportIcon color="error" />
          )}
        </Tooltip>
      ),
    },

    {
      headerName: ' ',
      field: 'send-email-alert',
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.statistics.oldestLead <= 0) return null;
        return (
          <CtaButton
            variant="contained"
            onClick={() => setMemberToSendNotification(row)}
          >
            <MailOutlineIcon />
            <Typography pl={1}>Notifica</Typography>
          </CtaButton>
        );
      },
      minWidth: 130,
      align: 'right',
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 50,
      sortable: false,
      align: 'right',
      renderCell: ({ row }) => {
        return (
          <Box display="flex" gap={1}>
            <IconButton
              id={`${row.id}-menu`}
              aria-controls={actionsMenuOpen ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={actionsMenuOpen ? 'true' : undefined}
              onClick={(event) => handleActionMenuClick(event, row)}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <div>
      <Paper sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', pt: 2, px: 2 }}>
          Membri del team ({rowCountMemoized})
        </Typography>
        <Divider sx={{ m: 2 }} />
        <DataGrid
          columns={tableColumns}
          rows={users || []}
          disableRowSelectionOnClick
          autoHeight
          rowCount={rowCountMemoized}
          loading={isPending}
          pageSizeOptions={[10]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
        />
      </Paper>
      <Menu
        anchorEl={anchorElActionsMenu}
        open={actionsMenuOpen}
        onClose={handleCloseActionsMenu}
        elevation={1}
      >
        <MenuItem onClick={handleRemoveMemberFromTeam} disabled={disabled}>
          Rimuovi membro dal team
        </MenuItem>
      </Menu>
      {memberToSendNotification && (
        <ConfirmActionModal
          open
          title="Invio alert email"
          description={`Sei sicuro di voler inviare una email di alert a ${memberToSendNotification.name}?`}
          handleClose={() => setMemberToSendNotification(null)}
          onConfirm={() =>
            onConfirmSendAlertNotificationToMember(memberToSendNotification)
          }
        />
      )}
    </div>
  );
};

export default TeamMembersTable;
